import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, FileClock } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Select, Btn, Input, Tag, EmptyState } from "@/components/kit";
import { api } from "@/lib/api";
import { fmtDate, formatReg } from "@/lib/vda";

const RECORD_TYPES = [
  ["operator_licence", "Operator licence"],
  ["insurance", "Insurance"],
  ["mot", "MOT certificate"],
  ["tax", "Vehicle tax"],
  ["service_plan", "Service plan"],
  ["tacho", "Tachograph"],
  ["driver_licence", "Driver licence check"],
  ["other", "Other"],
];
const STATUS_TONE = { valid: "green", expiring: "amber", expired: "red", renewed: "muted", na: "muted" };

export default function ComplianceRecords() {
  const [customers, setCustomers] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ record_type: "operator_licence", title: "", document_ref: "", expiry_date: "", renewal_due_date: "", vehicle_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.amCustomers().then((r) => { setCustomers(r.customers || []); if (r.customers?.length) setCustomerId(r.customers[0].id); }); }, []);
  const load = (cid) => { setLoading(true); api.complianceList(cid).then((r) => setRecords(r.records || [])).finally(() => setLoading(false)); };
  useEffect(() => { if (customerId) load(customerId); }, [customerId]);

  const add = async () => {
    if (!form.title && form.record_type === "other") { toast.error("Give the record a title."); return; }
    setSaving(true);
    try {
      await api.complianceCreate({
        customer_id: customerId, record_type: form.record_type,
        title: form.title || null, expiry_date: form.expiry_date,
        renewal_due_date: form.renewal_due_date || null,
        document_ref: form.document_ref || null, vehicle_id: form.vehicle_id || null,
      });
      toast.success("Compliance record added.");
      setForm({ record_type: "operator_licence", title: "", document_ref: "", expiry_date: "", renewal_due_date: "", vehicle_id: "" });
      load(customerId);
    } catch (e) { toast.error(e.message || "Could not save."); } finally { setSaving(false); }
  };
  const markRenewed = async (r) => { await api.complianceUpdate(r.id, { status: "renewed" }); toast.success("Marked renewed."); load(customerId); };
  const remove = async (r) => { await api.complianceDelete(r.id); toast.success("Removed."); load(customerId); };

  if (customers === null) return <StaffShell><Spinner /></StaffShell>;

  return (
    <StaffShell>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="vda-tick text-[hsl(var(--vda-clay))]">Compliance</p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Compliance documents</h1>
          <p className="text-sm text-muted-foreground">Expiry &amp; renewal records that feed the forward-looking fleet dashboard.</p>
        </div>
        <div className="min-w-[240px]">
          <label className="vda-tick text-[hsl(var(--vda-quiet))] block mb-1">Customer</label>
          <Select data-testid="compliance-customer" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </div>
      </div>

      <Panel className="p-4 mb-5" data-testid="compliance-add-form">
        <h2 className="font-semibold mb-3 inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Add a record</h2>
        <div className="grid md:grid-cols-3 gap-3">
          <div><label className="text-xs text-muted-foreground">Type</label>
            <Select data-testid="compliance-type" value={form.record_type} onChange={(e) => setForm({ ...form, record_type: e.target.value })}>
              {RECORD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select></div>
          <div><label className="text-xs text-muted-foreground">Title / reference</label>
            <Input data-testid="compliance-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. O-Licence OB1234567" /></div>
          <div><label className="text-xs text-muted-foreground">Expiry date</label>
            <Input data-testid="compliance-expiry" type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></div>
        </div>
        <div className="flex justify-end mt-3"><Btn data-testid="compliance-save" onClick={add} disabled={saving || !form.expiry_date}>{saving ? "Saving…" : "Add record"}</Btn></div>
      </Panel>

      {loading ? <Panel className="p-10 grid place-items-center"><Spinner /></Panel> : records.length === 0 ? (
        <EmptyState title="No compliance records" icon={FileClock} />
      ) : (
        <Panel className="overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
              <th className="px-3 py-2 font-medium">Type</th><th className="px-3 py-2 font-medium">Reference</th>
              <th className="px-3 py-2 font-medium">Vehicle</th><th className="px-3 py-2 font-medium">Expiry</th>
              <th className="px-3 py-2 font-medium">Status</th><th className="px-3 py-2"></th></tr></thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`compliance-row-${r.id}`}>
                  <td className="px-3 py-2">{(RECORD_TYPES.find((t) => t[0] === r.record_type) || [null, r.record_type])[1]}</td>
                  <td className="px-3 py-2">{r.title || r.document_ref || "—"}</td>
                  <td className="px-3 py-2 vda-reg">{r.vehicles?.registration ? formatReg(r.vehicles.registration) : "Fleet-wide"}</td>
                  <td className="px-3 py-2">{fmtDate(r.expiry_date)}</td>
                  <td className="px-3 py-2"><Tag tone={STATUS_TONE[r.status] || "muted"}>{r.status}</Tag></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.status !== "renewed" && <button data-testid={`compliance-renew-${r.id}`} onClick={() => markRenewed(r)} className="text-xs font-semibold text-primary hover:underline mr-3">Mark renewed</button>}
                    <button data-testid={`compliance-delete-${r.id}`} onClick={() => remove(r)} className="text-muted-foreground hover:text-[hsl(var(--vda-red))]"><Trash2 className="h-4 w-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </StaffShell>
  );
}
